import React, { useState, useEffect } from 'react';
import { FiAlertTriangle, FiCalendar, FiStar, FiBell, FiCheckCircle } from 'react-icons/fi';
import { formatDistanceToNow } from 'date-fns';
import { notificationService } from '../../services/api';
import './NotificationList.css';

const NotificationList = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterTab, setFilterTab] = useState('all');
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await notificationService.getAll({ limit: 1000 });
      setNotifications(response.data.notifications || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load notifications');
      console.error('Error fetching notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markRead(id);
      setNotifications(prev =>
        prev.map(n => (n.id === id ? { ...n, isRead: true } : n))
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'visa_expiry':
        return <FiAlertTriangle className="notification-icon warning" />;
      case 'leave_notification':
        return <FiCalendar className="notification-icon info" />;
      case 'performance_review':
        return <FiStar className="notification-icon purple" />;
      case 'general':
      default:
        return <FiBell className="notification-icon gray" />;
    }
  };

  const filteredNotifications = notifications.filter(notif => {
    if (filterTab === 'unread') {
      return !notif.isRead;
    }
    return true;
  });

  const unreadCount = notifications.filter(n => !n.isRead).length;

  if (loading && notifications.length === 0) {
    return (
      <div className="notification-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-container">
      <div className="notification-header">
        <div>
          <h1>Notifications</h1>
          {unreadCount > 0 && <span className="unread-badge">{unreadCount} unread</span>}
        </div>
        {unreadCount > 0 && (
          <button className="btn btn-sm btn-secondary" onClick={handleMarkAllAsRead}>
            <FiCheckCircle /> Mark All as Read
          </button>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="notification-tabs">
        <button
          className={`tab ${filterTab === 'all' ? 'active' : ''}`}
          onClick={() => setFilterTab('all')}
        >
          All
        </button>
        <button
          className={`tab ${filterTab === 'unread' ? 'active' : ''}`}
          onClick={() => setFilterTab('unread')}
        >
          Unread {unreadCount > 0 && <span className="tab-badge">{unreadCount}</span>}
        </button>
      </div>

      {filteredNotifications.length === 0 ? (
        <div className="empty-state">
          <FiBell size={48} className="empty-icon" />
          <h2>{filterTab === 'unread' ? 'No unread notifications' : 'No notifications'}</h2>
          <p>You are all caught up!</p>
        </div>
      ) : (
        <div className="notification-list">
          {filteredNotifications.map(notif => (
            <div
              key={notif.id}
              className={`notification-item ${notif.isRead ? '' : 'unread'}`}
              onClick={() => {
                if (!notif.isRead) {
                  handleMarkAsRead(notif.id);
                }
                setExpandedId(expandedId === notif.id ? null : notif.id);
              }}
            >
              <div className="notification-content">
                <div className="notification-icon-wrapper">
                  {getNotificationIcon(notif.type)}
                </div>

                <div className="notification-main">
                  <h3 className={`notification-title ${notif.isRead ? '' : 'bold'}`}>
                    {notif.title}
                  </h3>
                  <p className="notification-preview">{notif.message}</p>
                  <span className="notification-time">
                    {formatDistanceToNow(new Date(notif.createdAt), { addSuffix: true })}
                  </span>
                </div>

                {!notif.isRead && <div className="unread-dot"></div>}
              </div>

              {expandedId === notif.id && (
                <div className="notification-expanded">
                  <div className="expanded-message">{notif.message}</div>
                  {notif.actionUrl && (
                    <a href={notif.actionUrl} className="btn btn-sm btn-primary">
                      View Details
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificationList;
